'use client'

import SkeletonCard from '@/app/[locale]/_components/Skeleton/Card'
import { useResolveLocaleField } from '@/app/[locale]/_hooks/useResolveLocaleField'
import { IRedux } from '@/app/[locale]/_store/types'
import { ICategory, IChildCategory, IDiscount, IProduct } from '@/app/[locale]/_types'
import { Box, Button, HStack, SimpleGrid, Text } from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import { useParams, useRouter } from 'next/navigation'
import { memo, RefObject, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useIntersection } from 'react-use'
import ProductCard from '../../Card'

interface SectionProps {
  initial: boolean
  category: (ICategory | IChildCategory) | null
  products: IProduct[] | null
  discounts?: IDiscount[]
}

function Section({ initial, category, products, discounts }: SectionProps) {
  const router = useRouter()
  const params = useParams()

  const t = useTranslations()
  const resolveLocale = useResolveLocaleField()
  const settings = useSelector((state: IRedux) => state.settings)

  const [hasBeenInViewport, setHasBeenInViewport] = useState(false)

  const categoryNavigation = settings.data?.data?.category_navigation
  const isSeperatePage = categoryNavigation
    ? categoryNavigation === 'page'
    : settings.data?.data.menu_view === 'marketplace'

  const scrollMarginTop =
    settings?.data?.data?.address_placement === 'header_bottom'
      ? { base: '46px', lg: '0' }
      : category?.is_child
        ? { base: '46px', lg: '196px' }
        : 0

  const hasChildCategories =
    isSeperatePage &&
    category &&
    'child_categories' in category &&
    category?.child_categories &&
    category.child_categories.length > 0

  const interseptionRef = useRef<HTMLElement | null>(null)
  const intersection = useIntersection(
    interseptionRef as RefObject<HTMLElement>,
    {
      root: null,
      threshold: 0,
    }
  )

  useEffect(() => {
    if (intersection?.isIntersecting) {
      setHasBeenInViewport(true)
    }
  }, [intersection])

  return (
    <Box
      as='section'
      ref={interseptionRef}
      id={category?.slug}
      pt={category?.is_child ? 0 : { base: '130px', lg: '160px' }}
      mt={category?.is_child ? 0 : { base: '-130px', lg: '-130px' }}
      scrollMarginTop={scrollMarginTop}
    >
      <Box>
        <Text
          lineHeight={1}
          fontSize={{ base: 'xl', lg: category?.is_child ? '2xl' : '4xl' }}
          fontWeight={700}
          mt={{
            base: 4,
            lg: 8,
          }}
          mb={{ base: 4, lg: 4 }}
        >
          {resolveLocale(category?.title)}
        </Text>

        {/* Child category navigation */}
        {hasChildCategories && (
          <HStack
            mt={3}
            mb={4}
            w='fit-content'
            rounded='full'
            maxWidth='100%'
            flexWrap='wrap'
            justifyContent='flex-start'
          >
            {(category as ICategory).child_categories?.map((item) => (
              <Button
                bg={params?.child === item.slug ? 'primary' : 'var(--bg-card)'}
                color={params?.child === item.slug ? 'primary.text' : 'text'}
                fontWeight={500}
                key={item.id}
                rounded={12}
                size='xs'
                gap={2}
                px={4}
                onClick={() => {
                  if (params?.child === item.slug) {
                    router.push(`/category/${category.slug}`, {
                      scroll: false,
                    })
                  } else {
                    router.push(`/category/${category.slug}/${item.slug}`, {
                      scroll: false,
                    })
                  }
                }}
                variant={params?.child === item.slug ? 'solid' : 'outline'}
              >
                {resolveLocale(item?.title)}
              </Button>
            ))}
          </HStack>
        )}

        <SimpleGrid
          gapX={{ base: 3, lg: 6 }}
          gapY={{ base: 3, lg: 4 }}
          columns={{ base: 2, lg: 4 }}
        >
          {hasBeenInViewport || initial
            ? products?.map((product, idx) => (
              <ProductCard
                priority={initial && idx < 4}
                product={product}
                categoryName={resolveLocale(category?.title)}
                key={`${category?.id}-${product.id}`}
                discounts={discounts}
              />
            ))
            : Array.from({ length: products?.length ?? 8 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
        </SimpleGrid>
      </Box>
    </Box>
  )
}

export default memo(Section)
